import React, { useState, useCallback, useEffect } from 'react';
import { otherApi } from '../api';
import { Table, Button, Space, Input, Form, Modal, Select, message } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { FaUsers } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';

const columns = (editItem, deleteItem) => [
  {
    title: 'S.No',
    key: 'sno',
    render: (text, record, index) => index + 1,
    width: '10%',
  },
  {
    title: 'Vakaira Name',
    dataIndex: 'vakairaName',
    key: 'vakairaName',
    width: '20%',
  },
  {
    title: 'Sub Vakaira',
    dataIndex: 'subVakairaName',
    key: 'subVakairaName',
    width: '20%',
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => (
      <Space size="middle">
        <Button icon={<EditOutlined />} onClick={() => editItem(record)} />
        <Button icon={<DeleteOutlined />} onClick={() => deleteItem(record._id)} />
      </Space>
    ),
    width: '20%',
  },
];

const SubVakairs = () => {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [vakairaOptions, setVakairaOptions] = useState([]);

  useEffect(() => {
    otherApi.get('/sub-vakaira')
      .then(response => {
        if (response.data.success) {
          const responseData = Array.isArray(response.data.data) ? response.data.data : [];
          const normalizedData = responseData.map(item => ({
            _id: item._id,
            vakairaName: item.vakairaName,
            subVakairaName: item.subVakairaName,
          }));
          setData(normalizedData);
        } else {
          console.error('Data retrieval failed:', response.data.message);
        }
      })
      .catch(error => {
        console.error('There was an error fetching the data!', error);
      });
  }, []);

  useEffect(() => {
    fetchDataVakaira();
  }, []);

  const fetchDataVakaira = async () => {
    try {
      const response = await otherApi.get('/vakaira');
      if (response.data && Array.isArray(response.data.data)) {
        const options = response.data.data.map(v => ({
          value: v.vakairaName,
          label: v.vakairaName || 'Unknown'
        }));
        setVakairaOptions(options);
      } else {
        throw new Error('Unexpected response structure');
      }
    } catch (error) {
      console.error('Failed to fetch Vakaira data:', error);
      message.error('Failed to fetch Vakaira data. Please try again later.');
    }
  };

  const handleOk = useCallback(() => {
    form.validateFields()
      .then(values => {
        if (editingItem) {
          // Editing existing item
          otherApi.put(`/sub-vakaira/update`, {
            id: editingItem._id,
            subVakairaName: values.subVakairaName,
            vakairaName: values.vakairaName
          })
            .then(response => {
              if (response.data.success) {
                const updatedData = data.map(item =>
                  item._id === editingItem._id ? { ...item, ...values } : item
                );
                setData(updatedData);
                form.resetFields();
                setIsModalVisible(false);
                setEditingItem(null);
              } else {
                console.error('Update failed:', response.data.message);
                message.error('Update failed. Please try again later.');
              }
            })
            .catch(error => {
              console.error('There was an error updating the item!', error);
              message.error('There was an error updating the item. Please try again later.');
            });
        } else {
          // Adding new item
          otherApi.post('/sub-vakaira/addSubVakaira', values)
            .then(response => {
              if (response.data.success) {
                const newItem = {
                  _id: uuidv4(),
                  ...values,
                };
                setData(prevData => [...prevData, newItem]);
                form.resetFields();
                setIsModalVisible(false);
                setEditingItem(null);
              } else {
                console.error('Addition failed:', response.data.message);
                message.error('Addition failed. Please try again later.');
              }
            })
            .catch(error => {
              console.error('There was an error adding the item!', error);
              message.error('There was an error adding the item. Please try again later.');
            });
        }
      })
      .catch(info => console.log('Validate Failed:', info));
  }, [data, editingItem, form]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
    setEditingItem(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (editingItem) {
      otherApi.delete(`/sub-vakaira/deleteSubVakaira/${editingItem._id}`)
        .then(response => {
          if (response.data.success) {
            setData(prevData => prevData.filter(item => item._id !== editingItem._id));
            setIsConfirmDeleteVisible(false);
            setEditingItem(null);
            message.success('Item deleted successfully.');
          } else {
            console.error('Deletion failed:', response.data.message);
            message.error(`Deletion failed: ${response.data.message}`);
          }
        })
        .catch(error => {
          console.error('There was an error deleting the item!', error.response ? error.response.data : error.message);
          message.error(`Error: ${error.response ? error.response.data.message : error.message}`);
        });
    }
  }, [editingItem]);

  const handleDeleteCancel = useCallback(() => {
    setIsConfirmDeleteVisible(false);
    setEditingItem(null);
  }, []);

  const handleSearch = useCallback(e => {
    setSearchText(e.target.value);
  }, []);

  const filteredData = Array.isArray(data) ? data.filter(item =>
    (item.subVakairaName || '').toLowerCase().includes(searchText.toLowerCase())
  ) : [];

  const editItem = useCallback(record => {
    setEditingItem(record);
    form.setFieldsValue({ subVakairaName: record.subVakairaName, vakairaName: record.vakairaName });
    setIsModalVisible(true);
  }, [form]);

  const deleteItem = useCallback(id => {
    setEditingItem(data.find(item => item._id === id));
    setIsConfirmDeleteVisible(true);
  }, [data]);

  return (
    <div style={{ padding: '20px', boxSizing: 'border-box' }}>
      <h1 style={{ color: 'seagreen', textAlign: 'center', marginBottom: '20px' }}>
        <FaUsers /> Sub Vakairas
      </h1>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Input
          placeholder="Search Sub Vakaira"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearch}
          style={{ width: 200 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setEditingItem(null);
            setIsModalVisible(true);
          }}
        >
          Add Sub Vakaira
        </Button>
      </div>
      <Table
        columns={columns(editItem, deleteItem)}
        dataSource={filteredData}
        pagination={{ pageSize: 10 }}
        rowKey="_id"
      />
      <Modal
        title={editingItem ? "Edit Sub Vakaira" : "Add New Sub Vakaira"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            {editingItem ? 'Save Changes' : 'Submit'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          name="subVakairaForm"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item
            label="Vakaira Name"
            name="vakairaName"
            rules={[{ required: true, message: 'Please select the Vakaira Name!' }]}
          >
            <Select placeholder="Select Vakaira Name">
              {vakairaOptions.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Sub Vakaira"
            name="subVakairaName"
            rules={[{ required: true, message: 'Please input the Sub Vakaira!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Confirm Deletion"
        visible={isConfirmDeleteVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this item?</p>
      </Modal>
    </div>
  );
};

export default SubVakairs;
